import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { User, MapPin, Truck, Package } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { PublicAppSettings, PublicCheckout } from '@/api/publicEntities.js';

import MetashipWidget from '../components/MetashipWidget';
import DeliveryPointDisplay from '../components/DeliveryPointDisplay';
import { useDebounce, useCache, useDebounceAsync } from '../utils';

function useMetashipOffers() {
  const { setCache, getCache } = useCache(300_000);

  const fetchMetashipOffers = useCallback(async (packageInfo, address, deliveryPointNumber) => {
    if (!address && !deliveryPointNumber) {
      return {};
    }

    let params = {
      declaredValue: packageInfo.declaredValue,
      length: packageInfo.length,
      width: packageInfo.width,
      height: packageInfo.height,
      weight: packageInfo.weight,
      types: ['Courier', 'DeliveryPoint'],
      deliveryServiceCode: 'Cdek',
      address: address,
      errors: 1
    };

    if (deliveryPointNumber) {
      params.deliveryPointNumber = deliveryPointNumber;
    }

    // Try cache first
    const cacheKey = JSON.stringify(params);
    const cached = getCache(cacheKey);
    if (cached) {
      return cached;
    }

    // Call API
    const response = await PublicCheckout.getShippingOffers(params);
    const result = response || {};
    
    // Update cache
    setCache(cacheKey, result);
    
    return result;
  }, [getCache, setCache]);

  const {
    execute: getMetashipOffers,
    isLoading: isLoadingMetashipOffers,
    data: metashipOffers,
    error: metashipOffersError,
  } = useDebounceAsync(fetchMetashipOffers, 'Error loading Metaship offers:');

  return { metashipOffers, isLoadingMetashipOffers, getMetashipOffers, metashipOffersError };
}

export default function ShipmentStep({ cart, total, formData, onFieldChange, errors, lockedShipment }) {
  const [loadingDeliveryMethods, setLoadingDeliveryMethods] = useState(false);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(null);
  const { metashipOffers, isLoadingMetashipOffers, getMetashipOffers, metashipOffersError } = useMetashipOffers();
  const [cdekDeliveryPointDetails, setCdekDeliveryPointDetails] = useState(null);
  const [cdekCourierDeliveryDetails, setCdekCourierDeliveryDetails] = useState({
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Россия',
  });
  const debouncedCdekCourierDeliveryDetails = useDebounce(cdekCourierDeliveryDetails, 500);
  const { t } = useTranslation();
  const isLockedTargetPoint = lockedShipment?.type === 'target_point';

  // Calculate package dimensions and weight
  const packageInfo = useMemo(() => {
    // Суммируем вес в граммах из корзины
    const totalWeightGrams = cart.reduce((sum, item) => {
      const itemWeightGrams = item.metadata?.weight || item.weight || 500; // вес в граммах, fallback 500 грамм
      return sum + (itemWeightGrams * item.cartQuantity);
    }, 0);
    
    const itemCount = cart.reduce((sum, item) => sum + item.cartQuantity * (item.type == 'single' ? 1 : 5), 0);
    return {
      weight: totalWeightGrams / 1000, // конвертация в кг для CDEK API
      length: 21.0, // cm
      width: 14,  // cm
      height: 3.0 * itemCount, // cm
      declaredValue: total
    };
  }, [cart, total]);

  // Handle delivery method selection
  const handleDeliveryMethodChange = useCallback((methodType) => {
    setSelectedDeliveryMethod(methodType);
  }, []);

  const handlePointSelect = useCallback((point) => {
    console.log("Selected delivery point:", point);
    setCdekDeliveryPointDetails(point);
  }, []);

  const formattedCdekCourierDeliveryDetails = useMemo(() => {
    return [
      debouncedCdekCourierDeliveryDetails.country,
      debouncedCdekCourierDeliveryDetails.postal_code,
      debouncedCdekCourierDeliveryDetails.state,
      debouncedCdekCourierDeliveryDetails.city,
      debouncedCdekCourierDeliveryDetails.street,
    ]
      .map(part => (part || '').trim())
      .filter(part => part.length > 0)
      .join(', ');
  }, [debouncedCdekCourierDeliveryDetails]);

  // Check if courier address is complete (required fields filled)
  const isCourierAddressComplete = useMemo(() => {
    return debouncedCdekCourierDeliveryDetails.street.trim() !== '' && 
           debouncedCdekCourierDeliveryDetails.city.trim() !== '' &&
           JSON.stringify(debouncedCdekCourierDeliveryDetails) === JSON.stringify(cdekCourierDeliveryDetails);
  }, [debouncedCdekCourierDeliveryDetails, cdekCourierDeliveryDetails]);

  // Check if shipment selection is ready
  const isShipmentSelectionReady = useMemo(() => {
    if (isLockedTargetPoint) {
      return true;
    }
    // Condition 1: self pickup method is selected
    if (selectedDeliveryMethod?.startsWith('self_pickup')) {
      return true;
    }
    
    // Condition 2: delivery point method is selected and delivery point is selected and metaship offers are updated
    if (selectedDeliveryMethod === 'cdek_delivery_point') {
      return cdekDeliveryPointDetails &&
             !isLoadingMetashipOffers && 
             metashipOffers?.DeliveryPoint && 
             metashipOffers?.DeliveryPoint.length > 0;
    }
    
    // Condition 3: courier method is selected and address input is done and metaship offers are updated
    if (selectedDeliveryMethod === 'cdek_courier') {
      return isCourierAddressComplete && 
             !isLoadingMetashipOffers && 
             metashipOffers?.Courier && 
             metashipOffers?.Courier.length > 0;
    }
    
    return false;
  }, [
    selectedDeliveryMethod,
    cdekDeliveryPointDetails,
    isLoadingMetashipOffers,
    metashipOffers,
    isCourierAddressComplete,
  ]);

  // Update shipment_details when shipment selection is ready
  useEffect(() => {
    if (isLockedTargetPoint) {
      onFieldChange('shipment_details', lockedShipment);
      return;
    }
    if (!isShipmentSelectionReady) {
      // Clear shipment_details when not ready
      onFieldChange('shipment_details', null);
      return;
    }

    let shipmentData = null;
    
    if (selectedDeliveryMethod?.startsWith('self_pickup')) {
      // Find the self pickup method from delivery methods
      const selfPickupMethod = deliveryMethods[selectedDeliveryMethod.split('_')[2]];
      shipmentData = {
        type: 'self_pickup',
        data: {
          address: selfPickupMethod?.address || '',
          price: 0,
        }
      };
    } else if (selectedDeliveryMethod === 'cdek_delivery_point') {
      shipmentData = {
        type: 'cdek_delivery_point',
        data: {
          deliveryPoint: cdekDeliveryPointDetails,
          offer: metashipOffers?.DeliveryPoint?.[0],
          price: metashipOffers?.DeliveryPoint?.[0]?.service?.total || 0,
        }
      };
    } else if (selectedDeliveryMethod === 'cdek_courier') {
      shipmentData = {
        type: 'cdek_courier',
        data: {
          address: formattedCdekCourierDeliveryDetails,
          normalizedAddress: debouncedCdekCourierDeliveryDetails,
          offer: metashipOffers?.Courier?.[0],
          price: metashipOffers?.Courier?.[0]?.service?.total || 0,
        }
      };
    }
    
    if (shipmentData) {
      onFieldChange('shipment_details', shipmentData);
    }
  }, [
    metashipOffers,
    isLoadingMetashipOffers,
    isShipmentSelectionReady,
    selectedDeliveryMethod,
    cdekDeliveryPointDetails,
    debouncedCdekCourierDeliveryDetails,
    formattedCdekCourierDeliveryDetails,
    deliveryMethods,

    lockedShipment,
    isLockedTargetPoint,
    onFieldChange
  ]);

  // Load delivery methods when modal opens
  useEffect(() => {
    const loadDeliveryMethods = async () => {
      if (isLockedTargetPoint) {
        setDeliveryMethods([]);
        return;
      }
      setLoadingDeliveryMethods(true);
      try {
        const settings = await PublicAppSettings.list();
        const firstSettings = settings[0];
        if (firstSettings && firstSettings.delivery_methods) {
          setDeliveryMethods(firstSettings.delivery_methods);
        }
      } catch (error) {
        console.error('Error loading delivery methods:', error);
      } finally {
        setLoadingDeliveryMethods(false);
      }
    };

    loadDeliveryMethods();
  }, [isLockedTargetPoint]);

  // Metaship offers update logic
  useEffect(() => {
    if (isLockedTargetPoint) return;
    var updateMode = 'clear';
    if (debouncedCdekCourierDeliveryDetails.city && debouncedCdekCourierDeliveryDetails.city.length > 2) {
      updateMode = 'preliminary_city';
    }
    if (selectedDeliveryMethod === 'cdek_delivery_point' && cdekDeliveryPointDetails) {
      updateMode = 'delivery_point';
    }
    if (selectedDeliveryMethod === 'cdek_courier' && debouncedCdekCourierDeliveryDetails) {
      updateMode = 'courier_address';
    }

    switch (updateMode) {
      case 'clear':
        getMetashipOffers(packageInfo, null, null);
        break;
      case 'preliminary_city':
        getMetashipOffers(packageInfo, debouncedCdekCourierDeliveryDetails.city, null);
        break;
      case 'delivery_point':
        getMetashipOffers(packageInfo, cdekDeliveryPointDetails.name, cdekDeliveryPointDetails.serviceNumber);
        break;
      case 'courier_address':
        getMetashipOffers(packageInfo, formattedCdekCourierDeliveryDetails, null);
        break;
    }
  }, [selectedDeliveryMethod, cdekDeliveryPointDetails, debouncedCdekCourierDeliveryDetails, formattedCdekCourierDeliveryDetails, packageInfo, getMetashipOffers]);
    
  return (
    <div className="space-y-4">
      {/* Customer Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {t('checkout.customer_info.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name">{t('checkout.customer_info.name_label')} *</Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) => onFieldChange('customer_name', e.target.value)}
                placeholder={t('checkout.customer_info.name_placeholder')}
              />
            </div>
            <div>
              <Label htmlFor="customer_phone">{t('checkout.customer_info.phone_label')} *</Label>
              <Input
                id="customer_phone"
                value={formData.customer_phone}
                onChange={(e) => onFieldChange('customer_phone', e.target.value)}
                placeholder={t('checkout.customer_info.phone_placeholder')}
                maxLength={16}
                className={errors.customer_phone ? 'border-red-500' : ''}
              />
              <p className="text-sm text-slate-500 mt-1">{t('checkout.customer_info.phone_hint')}</p>
              {errors.customer_phone && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_phone}</p>
              )}
            </div>
          </div>
          
          <div>
            <Label htmlFor="customer_email">{t('checkout.customer_info.email_label')} *</Label>
            <Input
              id="customer_email"
              type="email"
              value={formData.customer_email}
              onChange={(e) => onFieldChange('customer_email', e.target.value)}
              placeholder={t('checkout.customer_info.email_placeholder')}
              className={errors.customer_email ? 'border-red-500' : ''}
            />
            {errors.customer_email && (
              <p className="text-sm text-red-600 mt-1">{errors.customer_email}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delivery Method Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            {t('checkout.delivery.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* City Field */}
          <div>
            <Label htmlFor="preliminary_city">{t('checkout.delivery.city_for_calculation')}</Label>
            <Input
              id="preliminary_city"
              value={cdekCourierDeliveryDetails.city}
              onChange={(e) => setCdekCourierDeliveryDetails(prev => ({ ...prev, city: e.target.value }))}
              placeholder={t('checkout.delivery.city_placeholder')}
                disabled={isLockedTargetPoint}
            />
            <p className="text-sm text-slate-500 mt-1">
              {t('checkout.delivery.city_help')}
            </p>
          </div>

          {isLockedTargetPoint ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold mb-1">{t('checkout.delivery.title')}</p>
              <p>
                Доставка будет выполнена в точку&nbsp;
                <span className="font-semibold">{lockedShipment?.data?.name}</span>. Вы не сможете изменить адрес для этого заказа.
              </p>
            </div>
          ) : loadingDeliveryMethods ? (
            <div className="text-center py-4">{t('checkout.delivery.loading_methods')}</div>
          ) : (
            <RadioGroup
              value={selectedDeliveryMethod}
              onValueChange={handleDeliveryMethodChange}
              className="space-y-3"
            >
              {deliveryMethods
                .filter(method => method.enabled)
                .map((method, index) => (
                  {
                    'self_pickup': {
                      id: `${method.type}_${index}`,
                      icon: <Package className="w-4 h-4" />,
                      label: `${t('checkout.delivery.self_pickup')}: ${method.address}`,
                      price: 0,
                    },
                    'cdek_delivery_point': {
                      id: method.type,
                      icon: <MapPin className="w-4 h-4" />,
                      label: t('checkout.delivery.cdek_delivery_point'),
                      price: metashipOffers?.DeliveryPoint?.[0]?.service.total,
                    },
                    'cdek_courier': {
                      id: method.type,
                      icon: <Truck className="w-4 h-4" />,
                      label: t('checkout.delivery.cdek_courier'),
                      price: metashipOffers?.Courier?.[0]?.service.total,
                    }
                  }[method.type]
                ))
                .map(({id, icon, label, price}) => {
                  return (
                    <div key={id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 min-h-[48px]">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value={id} id={id} />
                        <Label htmlFor={id} className="flex items-center gap-2 cursor-pointer">
                          {icon}
                          {label}
                        </Label>
                      </div>
                      <div className="text-right flex items-center min-h-[28px]">
                        {price === 0 ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            {t('checkout.delivery.free')}
                          </Badge>
                        ) : metashipOffersError ? (
                          <Badge variant="destructive" className="bg-red-100 text-red-800">
                            {t('general.error')}
                          </Badge>
                        ) : isLoadingMetashipOffers ? (
                          <div className="text-sm text-slate-500">{t('general.loading')}</div>
                        ) : price ? (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            ₽{price}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
            </RadioGroup>
          )}
        </CardContent>
      </Card>


      {/* Delivery Point Selection */}
      {selectedDeliveryMethod === 'cdek_delivery_point' && !cdekDeliveryPointDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('checkout.delivery.select_delivery_point')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MetashipWidget onPointSelect={handlePointSelect} fullAddress={formattedCdekCourierDeliveryDetails} />
          </CardContent>
        </Card>
      )}

      {/* Delivery Point Selected */}
      {selectedDeliveryMethod === 'cdek_delivery_point' && cdekDeliveryPointDetails && (
        <DeliveryPointDisplay
          point={cdekDeliveryPointDetails}
          onChange={() => setCdekDeliveryPointDetails(null)}
          showChangeButton={true}
        />
      )}

      {/* Courier Address Input */}
      {selectedDeliveryMethod === 'cdek_courier' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('checkout.delivery.address_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="street">{t('checkout.delivery.address_label')} *</Label>
              <Input
                id="street"
                value={cdekCourierDeliveryDetails.street}
                onChange={(e) => setCdekCourierDeliveryDetails(prev => ({ ...prev, street: e.target.value }))}
                placeholder={t('checkout.delivery.address_placeholder')}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">{t('checkout.delivery.city_label')} *</Label>
                <Input
                  id="city"
                  value={cdekCourierDeliveryDetails.city}
                  onChange={(e) => setCdekCourierDeliveryDetails(prev => ({ ...prev, city: e.target.value }))}
                  placeholder={t('checkout.delivery.city_placeholder')}
                />
              </div>
              <div>
                <Label htmlFor="state">{t('checkout.delivery.state_label')}</Label>
                <Input
                  id="state"
                  value={cdekCourierDeliveryDetails.state}
                  onChange={(e) => setCdekCourierDeliveryDetails(prev => ({ ...prev, state: e.target.value }))}
                  placeholder={t('checkout.delivery.state_placeholder')}
                />
              </div>
              <div>
                <Label htmlFor="postal_code">{t('checkout.delivery.postal_code_label')}</Label>
                <Input
                  id="postal_code"
                  value={cdekCourierDeliveryDetails.postal_code}
                  onChange={(e) => setCdekCourierDeliveryDetails(prev => ({ ...prev, postal_code: e.target.value }))}
                  placeholder={t('checkout.delivery.postal_code_placeholder')}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
